import { DateTime } from 'luxon'

import { TripPayload, TripType } from 'store/trips/trips.types'
import { convertToTitleCase } from '../../../../trip.utils'

export const shouldSeparateOrderTasks = (
  tripTasks: TripPayload[],
  index: number
): boolean => {
  if (index === 0) return false

  const currentTask = tripTasks[index]
  const previousTask = tripTasks[index - 1]

  return (
    !!currentTask?.orderId &&
    !!previousTask?.orderId &&
    currentTask.orderId !== previousTask.orderId
  )
}

export const sharesOrderWithNextTask = (
  tripTasks: TripPayload[],
  index: number
): boolean => {
  const currentTask = tripTasks[index]
  const nextTask = tripTasks[index + 1]

  return (
    !!currentTask?.orderId &&
    !!nextTask?.orderId &&
    currentTask.orderId === nextTask.orderId
  )
}

export const isPickupOrDelivery = (type: TripPayload['type']) =>
  type === 'PICKUP' || type === 'DELIVERY'

export const isOrderLinkedHookTrailerTask = (task: TripPayload): boolean =>
  task.type === 'HOOK_TRAILER' && task.orderEventId != null && !!task.orderId

export const isOrderLinkedDropTrailerTask = (task: TripPayload): boolean =>
  task.type === 'DROP_TRAILER' &&
  task.orderEventId != null &&
  !!task.orderId

export const isEnteredDropTrailerTask = (task: TripPayload): boolean =>
  task.type === 'DROP_TRAILER' &&
  task.sourceOrderPkId != null &&
  task.orderEventId == null

export const isEnteredHookTrailerTask = (task: TripPayload): boolean =>
  task.type === 'HOOK_TRAILER' &&
  task.sourceOrderPkId != null &&
  task.orderEventId == null

const EQUIPMENT_TASK_TYPES: TripType[] = [
  'HOOK_TRACTOR',
  'DROP_TRACTOR',
  'HOOK_TRAILER',
  'DROP_TRAILER',
]

export const isEquipmentTaskType = (type: TripType) =>
  EQUIPMENT_TASK_TYPES.includes(type)

export const isEquipmentOnlyTask = (task: TripPayload): boolean =>
  isEquipmentTaskType(task.type) &&
  task.orderEventId == null &&
  task.sourceOrderPkId == null &&
  !task.orderId

export const getEquipmentTaskLabel = (type: TripType): string => {
  const [action, equipment] = type.split('_')
  return `${convertToTitleCase(action)} ${convertToTitleCase(equipment)}`
}

export const showsOrderEventDetails = (tripTask: TripPayload) =>
  isPickupOrDelivery(tripTask.type) ||
  isOrderLinkedHookTrailerTask(tripTask) ||
  isOrderLinkedDropTrailerTask(tripTask) ||
  isEnteredDropTrailerTask(tripTask) ||
  isEnteredHookTrailerTask(tripTask)

export const showsOrderId = (tripTask: TripPayload) => showsOrderEventDetails(tripTask)

export const showsTaskStatus = (type: TripPayload['type']) =>
  type === 'PICKUP' || type === 'DELIVERY'

export const showsTripTaskStatus = (tripTask: TripPayload) =>
  showsTaskStatus(tripTask.type) ||
  isOrderLinkedHookTrailerTask(tripTask) ||
  isOrderLinkedDropTrailerTask(tripTask) ||
  isEnteredDropTrailerTask(tripTask) ||
  isEnteredHookTrailerTask(tripTask)

const getCompletedStatusLabel = (tripTask: TripPayload) => {
  if (isOrderLinkedHookTrailerTask(tripTask)) {
    return 'Actual Hook'
  }

  switch (tripTask.type) {
    case 'PICKUP':
      return 'Actual Pickup'
    case 'DELIVERY':
      return 'Actual Delivery'
    case 'DROP_TRAILER':
      return 'Actual Drop'
    default:
      return ''
  }
}

export const getMarkAsCompletedLabel = (tripTask: TripPayload): string => {
  switch (tripTask.type) {
    case 'PICKUP':
      return 'Mark as Picked Up'
    case 'DELIVERY':
      return 'Mark as Delivered'
    case 'DROP_TRAILER':
      return 'Mark as Dropped'
    case 'HOOK_TRAILER':
      return 'Mark as Hooked'
    default:
      return 'Mark as completed'
  }
}

export const formatTripEventDateTime = (startTime?: string) => {
  if (!startTime) return { date: '', time: '' }

  const dt = DateTime.fromISO(startTime)
  if (!dt.isValid) return { date: '', time: '' }

  return {
    date: dt.toFormat('LLL dd, yyyy'),
    time: dt.toFormat('hh:mm a'),
  }
}

export const formatTripTaskStatus = (tripTask: TripPayload) => {
  if (!showsTripTaskStatus(tripTask)) {
    return { value1: '', value2: '' }
  }

  if (tripTask.taskStatus !== 'COMPLETED') {
    return { value1: 'Pending', value2: '' }
  }

  if (!tripTask.modifiedDate) {
    return { value1: 'Pending', value2: '' }
  }

  const dt = DateTime.fromISO(tripTask.modifiedDate, { zone: 'utc' }).toLocal()
  if (!dt.isValid) {
    return { value1: 'Pending', value2: '' }
  }

  return {
    value1: getCompletedStatusLabel(tripTask),
    value2: `${dt.toFormat('LLL dd, yyyy')} ${dt.toFormat('hh:mm a')}`,
  }
}
